import { SecurityStackReportIcon } from 'ui/components/svg'
import ReportCardV2 from '../../components/report-card-v2/ReportCardV2'
import { FC, useEffect, useRef, useState, useMemo } from 'react'
import { useAppDispatch } from '../../../../../store/hooks'
import { useQuery } from 'react-query'
import { ReportServiceV1 } from 'services'
import { ToastVariant, openToast, closeToast } from '../../../../../store/toasts/toastSlice'
import { downloadFile } from '../../../reports/reports.utils'
import { useAuth } from '@frontegg/react'
import { hasAnyRole, Role } from '../../../../../lib/utils'
import toast from 'react-hot-toast'
import { HotToast } from 'ui/components/toast'

interface SecurityStackReportProps {
  noActiveCpis?: boolean
}

const SecurityStackReport: FC<SecurityStackReportProps> = ({ noActiveCpis }) => {
  const { user } = useAuth()
  const dispatch = useAppDispatch()
  const closeNotification = useRef<any>(null)

  const [ssmReportCode, setSsmReportCode] = useState('')
  const [isGeneratingSSMReport, setIsGeneratingSSMReport] = useState(false)

  const ssmReportQuery = useQuery(
    ['ssmReportCode', { ssmReportCode }],
    () => ReportServiceV1.downloadReport(ssmReportCode),
    {
      refetchInterval: 5000,
      enabled: !!ssmReportCode
    }
  )

  const hasPermissionToGenerateReport = useMemo(() => {
    const allowedRoles = [Role.Owner, Role.Admin, Role.Demo]
    const hasRole = hasAnyRole(allowedRoles, user)
    return hasRole
  }, [user])

  const generateSSMReport = async () => {
    if (noActiveCpis) {
      dispatch(
        openToast({
          variant: ToastVariant.warning,
          props: {
            text: 'Report Generation Error',
            description:
              'No active CPIs were found. Please activate CPIs to generate a report.'
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      return
    }

    try {
      setIsGeneratingSSMReport(true)
      const res = await ReportServiceV1.generateSsmReport()
      if (res.status.toString().startsWith('2')) {
        const { code } = res.data
        setSsmReportCode(code)
      }
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
    } finally {
      setIsGeneratingSSMReport(false)
    }
  }

  // Polling for report generation errors
  useEffect(() => {
    if (ssmReportQuery.error && (ssmReportQuery.error as any).response?.status === 418) {
      setIsGeneratingSSMReport(false)
      setSsmReportCode('')
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: (ssmReportQuery.error as any).response?.data.error || 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    }
  }, [ssmReportQuery.error])

  // Polling for successful report download URL
  useEffect(() => {
    if (ssmReportQuery.data && !ssmReportQuery.error && !ssmReportQuery.isFetching) {
      const downloadUrl = ssmReportQuery.data.data.reportDownloadUrl.url
      downloadFile(`${downloadUrl}`, 'SSM Report')
      toast.success(() => <HotToast text="SSM Report PDF Successfully Downloaded" />)
      setSsmReportCode('')
    }
  }, [ssmReportQuery.data, ssmReportQuery.error, ssmReportQuery.isFetching])

  return (
    <>
      <ReportCardV2
        reportIcon={<SecurityStackReportIcon />}
        title="Security Stack Report"
        description="Detailed summary of your security stack coverage and budget allocation."
        isLoading={isGeneratingSSMReport || ssmReportQuery.isFetching}
        hasPermission={hasPermissionToGenerateReport}
        onDownload={generateSSMReport}
      />
    </>
  )
}

export default SecurityStackReport
